import React, { useState } from 'react';
import { Upload, Loader2 } from 'lucide-react';
import { uploadFile } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

/**
 * Reusable drag-and-drop dropzone.
 * Calls onUpload(url, file) for each uploaded file.
 */
export const Dropzone = ({
  onUpload,
  multiple = false,
  accept = 'image/*',
  label = 'Drag and drop files here, or click to browse',
  compact = false,
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { toast } = useToast();
  const inputId = `dropzone-${Math.random().toString(36).slice(2, 9)}`;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleFiles = async (files) => {
    const arr = Array.from(files);
    setUploading(true);
    try {
      for (const file of arr) {
        const url = await uploadFile(file);
        await onUpload(url, file);
      }
      toast({ title: 'Upload complete', description: `${arr.length} file(s) uploaded` });
    } catch (err) {
      toast({
        title: 'Upload failed',
        description: err?.response?.data?.detail || err.message,
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) handleFiles(e.dataTransfer.files);
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files.length) handleFiles(e.target.files);
    e.target.value = '';
  };

  return (
    <div
      data-testid="dropzone"
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
      onClick={() => document.getElementById(inputId).click()}
      className={`cursor-pointer border-2 border-dashed rounded-lg text-center transition-colors ${
        compact ? 'p-4' : 'p-10'
      } ${
        dragActive ? 'border-[#1565C0] bg-blue-50' : 'border-gray-300 hover:border-[#1565C0]/60 bg-gray-50'
      }`}
    >
      <input
        id={inputId}
        type="file"
        multiple={multiple}
        accept={accept}
        className="hidden"
        onChange={handleChange}
        data-testid="dropzone-input"
      />
      {uploading ? (
        <div className="flex flex-col items-center gap-2 text-[#1565C0]">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-sm">Uploading…</span>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2 text-gray-600">
          <Upload className={compact ? 'w-5 h-5' : 'w-8 h-8'} />
          <span className="text-sm">{label}</span>
        </div>
      )}
    </div>
  );
};

export default Dropzone;
